import { useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'

import { usePingUnit } from '@apps-common/apis/portal/control'
import { useUpdateUnitTechCompany } from '@apps-common/apis/portal/unit'
import { AccountChangeModal } from '@apps-common/apps/portal/pages/units/modals'
import { DetailsItem, DetailsItemAction } from '@apps-common/apps/portal/pages/units/pages/manage/components/details'
import { ImageComponent } from '@apps-common/components/common'
import { CompanySelectModal } from '@apps-common/components/modals'
import { SiteDetailsModal } from '@apps-common/components/modals/site/SiteDetailsModal'
import { AppType } from '@apps-common/config/app-types'
import { CompanyType } from '@apps-common/config/company'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_INVALID_SERIAL_NO_VALUES } from '@apps-common/config/unit'
import { invalidateAllUnitQueries } from '@apps-common/helpers/invalidate-unit-queries'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { unitIsActive, unitIsCancelled, unitIsInactive } from '@apps-common/helpers/unit-helpers'
import { valueIsValidCheck, valueStringCheck } from '@apps-common/helpers/value-string-check'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

interface IProps {
  loading?: boolean
  unit?: IUnitGenericDetailsModel
}

export const UnitDetailsManagementBody: React.FC<IProps> = ({ loading, unit }) => {
  const {
    unitId,
    product,
    imageUrl,
    siteName,
    mCentre,
    dateLoaded,
    dateDeleted,
    planName,
    bureau,
    techCompany,
    accountNo,
    mobileNo,
    imei,
    serialNo
  } = unit || {}

  const {
    isLoading: pingLoading,
    isFetching: pingFetching,
    isRefetching: pingRefetching,
    isError: pingError,
    data: pingData
  } = usePingUnit({
    config: {
      enabled: !!unit
    },
    model: {
      unitId: unit?.unitId || '',
      triggerType: '',
      onTime: 0,
      commands: []
    }
  })

  const deviceOnline = !pingLoading && !pingFetching && !pingRefetching && !pingError && pingData?.pingResult

  const [presentSiteDetails, dismissSiteDetails] = useIonModal(SiteDetailsModal, {
    componentProps: { unitId },
    onDismiss: (data: string, role: string) => dismissSiteDetails(data, role),
    enableBackdropDismiss: false
  })

  const [presentTechChange, dismissTechChange] = useIonModal(CompanySelectModal, {
    componentProps: {
      subtitle: 'Update',
      title: 'Technician',
      companyType: CompanyType.Technician,
      noResultsDescription: 'Search above for technician companies'
    },
    onDismiss: (data: string, role: string) => dismissTechChange(data, role),
    enableBackdropDismiss: false
  })

  const [presentAccountChange, dismissAccountChange] = useIonModal(AccountChangeModal, {
    componentProps: { unitId, caller: 'management' },
    onDismiss: (data: string, role: string) => dismissAccountChange(data, role),
    enableBackdropDismiss: false
  })

  const { mutate: updateTechCompany, isPending: isUpdatingTechCompany } = useUpdateUnitTechCompany(true, () => {
    invalidateAllUnitQueries(unit?.unitId || '', AppType.management)
  })

  const isActive = unitIsActive(unit)
  const isInactive = unitIsInactive(unit)
  const isCancelled = unitIsCancelled(unit)

  return (
    <div className='grid grid-cols-2 gap-[12px] sm:gap-[16px] md:gap-[12px] lg:gap-x-[20px] lg:gap-y-[23px]'>
      <div className='hidden sm:flex relative left-[-10px]'>
        {loading && (
          <div className='bg-skeleton rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px] animate-pulse'>
            <div className='bg-skeleton-hi p-[10px] rounded-full w-[50px] h-[50px]' />
            <div className='grow flex flex-col gap-[3px]'>
              <div className='bg-skeleton-hi w-[45px] h-[14px] rounded' />
              <div className='bg-skeleton-hi w-[65px] h-[18px] rounded' />
            </div>
          </div>
        )}
        {!loading && (
          <div className='bg-surface border border-border rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px]'>
            <ImageComponent
              src={imageUrl}
              alt={product || 'Product name'}
              containerClasses='bg-surface-2 border border-border p-[10px] rounded-full w-[50px] h-[50px]'
              imageClasses='w-[32px] h-[32px]'
            />
            <div>
              <div className='text-text text-[0.8em] jost'>Product</div>
              <div className='text-text text-[0.9em] jost-light'>{product}</div>
            </div>
          </div>
        )}
      </div>
      <DetailsItemAction
        label='Site'
        value={valueStringCheck(siteName)}
        allowCopy={valueIsValidCheck(siteName)}
        classes=''
        callbackHidden={isCancelled}
        callback={() => {
          presentSiteDetails({
            enterAnimation: modalSlideUpEnterAnimation,
            leaveAnimation: modalSlideUpLeaveAnimation,
            backdropDismiss: false,
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
              if (ev.detail.role === ModalDismiss.Confirm) {
                invalidateAllUnitQueries(unitId || '', AppType.management)
              }
            }
          })
        }}
        loading={loading}
      />
      <DetailsItem label='Monitoring centre' value={valueStringCheck(mCentre)} allowCopy={valueIsValidCheck(mCentre)} loading={loading} />
      <DetailsItem label='Bureau' value={valueStringCheck(bureau)} allowCopy={valueIsValidCheck(bureau)} loading={loading} />
      <DetailsItemAction
        label='Technician'
        value={valueStringCheck(techCompany)}
        allowCopy={valueIsValidCheck(techCompany)}
        callbackHidden={isCancelled}
        callback={() =>
          presentTechChange({
            enterAnimation: modalSlideUpEnterAnimation,
            leaveAnimation: modalSlideUpLeaveAnimation,
            backdropDismiss: false,
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
              if (ev.detail.role === ModalDismiss.Confirm) {
                updateTechCompany({
                  unitId: unitId || '',
                  techCompanyId: ev?.detail.data
                })
              }
            }
          })
        }
        loading={loading || isUpdatingTechCompany}
      />
      <DetailsItem label='Product' value={product} classes='hidden sm:block' loading={loading} />
      <DetailsItem label='Monthly plan' value={planName} classes='hidden sm:block' loading={loading} />
      {(isInactive || isActive) && (
        <DetailsItem
          label={isInactive ? 'Registered on' : 'Activated on'}
          value={toLocalTimestampStr(dateLoaded || '', 'DD MMM YY')}
          classes='hidden sm:block'
          loading={loading}
        />
      )}
      {isCancelled && (
        <DetailsItem
          label='Cancelled on'
          value={toLocalTimestampStr(dateDeleted || '', 'DD MMM YY')}
          classes='hidden sm:block'
          loading={loading}
        />
      )}
      <DetailsItemAction
        label='Account number'
        value={valueStringCheck(isInactive ? null : accountNo)}
        allowCopy={!isInactive || valueIsValidCheck(accountNo)}
        callbackDisabled={!isActive || !deviceOnline}
        callback={() =>
          presentAccountChange({
            enterAnimation: modalSlideUpEnterAnimation,
            leaveAnimation: modalSlideUpLeaveAnimation,
            backdropDismiss: false,
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
              if (ev.detail.role === ModalDismiss.Confirm) {
                invalidateAllUnitQueries(unitId || '', AppType.management)
              }
            }
          })
        }
        classes='hidden sm:block'
        loading={loading}
      />
      <DetailsItem
        label='Mobile number'
        value={valueStringCheck(mobileNo)}
        allowCopy={valueIsValidCheck(mobileNo)}
        classes='hidden sm:block'
        loading={loading}
      />
      <DetailsItem
        label='IMEI number'
        value={valueStringCheck(imei)}
        allowCopy={valueIsValidCheck(imei)}
        classes='hidden sm:block'
        loading={loading}
      />
      <DetailsItem
        classes='hidden md:block'
        label='Serial'
        value={valueStringCheck(serialNo, UNIT_INVALID_SERIAL_NO_VALUES)}
        allowCopy={valueIsValidCheck(serialNo, UNIT_INVALID_SERIAL_NO_VALUES)}
        loading={loading}
      />
    </div>
  )
}
